import { createTheme } from '@mui/material/styles';

import { THEME_DEFAULT_ACCENT_COLOR } from '../../constants/shell';
import { ITheme } from '../../types/shell';
import { isLightColor } from '../../utils/ui';
import variables from './variables';

// Material Design standard easing function for react-spring
// Matches cubic-bezier(0.4, 0, 0.2, 1)
export const materialStandardEasing = (t: number): number => {
  const p1 = 0.4;
  const p2 = 0.2;
  // Cubic Bezier curve with control points (0, 0), (p1, 0), (p2, 1), (1, 1)
  const omt = 1 - t;
  const p2Adjusted = 1 - p2;
  return omt * omt * t * p1 * 3 + omt * t * t * p2Adjusted * 3 + t * t * t;
};

const getThemeConfig = (theme?: ITheme) =>
  createTheme({
    wl: theme,
    sass: variables,
    // Custom easing function for react-spring animations in non-MUI components (Drawer component)
    // Equivalent to cubic-bezier(0.4, 0, 0.2, 1)
    easing: {
      standard: materialStandardEasing,
    },
    palette: {
      primary: {
        main: theme?.portal_accent_color || THEME_DEFAULT_ACCENT_COLOR,
        contrastText: isLightColor(theme?.portal_accent_color || THEME_DEFAULT_ACCENT_COLOR)
          ? variables.ebony
          : variables.white,
      },
      text: {
        primary: variables.ebony,
      },
      action: {
        active: variables.muiIconActive,
      },
    },
    typography: {
      fontFamily: 'Gilroy, sans-serif',
    },
    // MUI's CSS transitions config (used by MUI components)
    // Note: transitions.easing.easeInOut is CSS string, easing.standard is JS function
    transitions: {
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)', // Material Design standard easing (CSS)
      },
      duration: {
        enteringScreen: 225, // expand
        leavingScreen: 195, // collapse
      },
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: variables.ebony,
            },
            '& .MuiOutlinedInput-root': {
              borderRadius: 4,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            /* Helper text position is absolute and below textField is not creating extra space*/
            '& .MuiFormHelperText-root': {
              position: 'absolute',
              bottom: -20,
              left: 0,
              margin: 0,
            },
            /* Autofill background color is transparent */
            '& input:-webkit-autofill': {
              backgroundColor: 'transparent !important',
              transition: 'background-color 5000s ease-in-out 0s',
            },
            '& input:-webkit-autofill:hover': {
              backgroundColor: 'transparent !important',
              transition: 'background-color 5000s ease-in-out 0s',
            },
            '& input:-webkit-autofill:focus': {
              backgroundColor: 'transparent !important',
              transition: 'background-color 5000s ease-in-out 0s',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 100,
            '&.Mui-disabled': {
              opacity: 1,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            textTransform: 'none',
            fontWeight: 500,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            zIndex: 0,
            borderRadius: 4,
          },
          /* Red asterisk(*) when field is required */
          asterisk: {
            color: variables.errorRed,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: variables.muiIconActive,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 4,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 4,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: 4,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },
    },
  });

export default getThemeConfig;
